import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import { cn } from "./utils";

const badgeVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap font-semibold rounded-lg duration-normal focus:outline-none focus:ring-2 focus:ring-mebi-yellow-500/20 focus:ring-offset-2",
  {
    variants: {
      variant: {
        filled: "",
        outlined: "border bg-white",
        subtle: "",
      },
      badgeColor: {
        green: "",
        blue: "",
        yellow: "",
        orange: "",
        red: "",
        purple: "",
        gray: "",
      },      size: {
        sm: "h-[18px] px-2 gap-1 text-[10px] leading-none",
        lg: "h-[30px] px-3 gap-1.5 text-xs",
      },
    },
    compoundVariants: [
      // GREEN variants - Success states (MEBI design tokens)
      {
        variant: "filled",
        badgeColor: "green",
        class: "bg-mebi-green-500 text-white hover:bg-mebi-green-600",
      },
      {
        variant: "outlined",
        badgeColor: "green",
        class:
          "border-mebi-green-500 text-mebi-green-600 hover:bg-mebi-green-100",
      },
      {
        variant: "subtle",
        badgeColor: "green",
        class: "bg-mebi-green-100 text-mebi-green-700 hover:bg-mebi-green-200",
      },

      // BLUE variants - Active/Primary states (MEBI design tokens)
      {
        variant: "filled",
        badgeColor: "blue",
        class: "bg-mebi-blue-500 text-white hover:bg-mebi-blue-600",
      },
      {
        variant: "outlined",
        badgeColor: "blue",
        class: "border-mebi-blue-500 text-mebi-blue-600 hover:bg-mebi-blue-100",
      },
      {
        variant: "subtle",
        badgeColor: "blue",
        class: "bg-mebi-blue-100 text-mebi-blue-700 hover:bg-mebi-blue-200",
      },

      // YELLOW variants - MEBI brand color (using MEBI tokens)
      {
        variant: "filled",
        badgeColor: "yellow",
        class: "bg-mebi-yellow-500 text-white hover:bg-mebi-yellow-600",
      },
      {
        variant: "outlined",
        badgeColor: "yellow",
        class:
          "border-mebi-yellow-500 text-mebi-yellow-600 hover:bg-mebi-yellow-100",
      },
      {
        variant: "subtle",
        badgeColor: "yellow",
        class:
          "bg-mebi-yellow-100 text-mebi-yellow-700 hover:bg-mebi-yellow-200",
      },

      // ORANGE variants - Warning states (MEBI design tokens)
      {
        variant: "filled",
        badgeColor: "orange",
        class: "bg-mebi-orange-500 text-white hover:bg-mebi-orange-600",
      },
      {
        variant: "outlined",
        badgeColor: "orange",
        class:
          "border-mebi-orange-500 text-mebi-orange-600 hover:bg-mebi-orange-100",
      },
      {
        variant: "subtle",
        badgeColor: "orange",
        class:
          "bg-mebi-orange-100 text-mebi-orange-700 hover:bg-mebi-orange-200",
      },
      // RED variants - Error/Cancelled states (MEBI design tokens)
      {
        variant: "filled",
        badgeColor: "red",
        class: "bg-mebi-red-500 text-white hover:bg-mebi-red-600",
      },
      {
        variant: "outlined",
        badgeColor: "red",
        class: "border-mebi-red-500 text-mebi-red-600 hover:bg-mebi-red-100",
      },
      {
        variant: "subtle",
        badgeColor: "red",
        class: "bg-mebi-red-100 text-mebi-red-700 hover:bg-mebi-red-200",
      },

      // PURPLE variants - Special states (MEBI design tokens)
      {
        variant: "filled",
        badgeColor: "purple",
        class: "bg-mebi-purple-500 text-white hover:bg-mebi-purple-600",
      },
      {
        variant: "outlined",
        badgeColor: "purple",
        class:
          "border-mebi-purple-500 text-mebi-purple-600 hover:bg-mebi-purple-100",
      },
      {
        variant: "subtle",
        badgeColor: "purple",
        class:
          "bg-mebi-purple-100 text-mebi-purple-700 hover:bg-mebi-purple-200",
      },
      // GRAY variants - Neutral/Default states (MEBI design tokens)
      {
        variant: "filled",
        badgeColor: "gray",
        class: "bg-neutral-600 text-white hover:bg-neutral-700",
      },
      {
        variant: "outlined",
        badgeColor: "gray",
        class: "border-neutral-300 text-neutral-600 hover:bg-neutral-100",
      },
      {
        variant: "subtle",
        badgeColor: "gray",
        class: "bg-neutral-100 text-neutral-600 hover:bg-neutral-200",
      },
    ],    defaultVariants: {
      variant: "filled",
      badgeColor: "gray",
      size: "sm",
    },
  },
);

type StatusConfig = {
  label: string;
  badgeColor:
    | "green"
    | "blue"
    | "yellow"
    | "orange"
    | "red"
    | "purple"
    | "gray";
  variant: "filled" | "outlined" | "subtle";
};

// Status mappings with Vietnamese labels - MEBI Specification
const STATUS_MAPPINGS = {
  // Giải đấu (Tournament Status) - 11 trạng thái
  tournament: {
    draft: {
      label: "Nháp",
      badgeColor: "gray",
      variant: "outlined",
    } as StatusConfig,
    planned: {
      label: "Đã lên kế hoạch",
      badgeColor: "blue",
      variant: "outlined",
    } as StatusConfig,
    registration_open: {
      label: "Mở đăng ký",
      badgeColor: "green",
      variant: "filled",
    } as StatusConfig,
    registration_full: {
      label: "Đủ người",
      badgeColor: "purple",
      variant: "filled",
    } as StatusConfig,
    registration_expired: {
      label: "Hết hạn đăng ký",
      badgeColor: "orange",
      variant: "filled",
    } as StatusConfig,
    registration_closed: {
      label: "Đóng đăng ký",
      badgeColor: "red",
      variant: "outlined",
    } as StatusConfig,
    upcoming: {
      label: "Sắp diễn ra",
      badgeColor: "blue",
      variant: "filled",
    } as StatusConfig,
    ongoing: {
      label: "Đang diễn ra",
      badgeColor: "green",
      variant: "filled",
    } as StatusConfig,
    postponed: {
      label: "Bị hoãn",
      badgeColor: "yellow",
      variant: "filled",
    } as StatusConfig,
    cancelled: {
      label: "Đã hủy",
      badgeColor: "red",
      variant: "filled",
    } as StatusConfig,
    completed: {
      label: "Đã kết thúc",
      badgeColor: "gray",
      variant: "filled",
    } as StatusConfig,
  },

  // Đăng ký giải đấu (Tournament Registration Status) - 7 trạng thái
  registration: {
    pending_approval: {
      label: "Chờ duyệt",
      badgeColor: "blue",
      variant: "outlined",
    } as StatusConfig,
    profile_approved: {
      label: "Đã duyệt hồ sơ",
      badgeColor: "green",
      variant: "outlined",
    } as StatusConfig,
    payment_pending: {
      label: "Chưa đóng phí",
      badgeColor: "orange",
      variant: "outlined",
    } as StatusConfig,
    payment_confirmed: {
      label: "Đã xác nhận phí",
      badgeColor: "green",
      variant: "filled",
    } as StatusConfig,
    rejected: {
      label: "Từ chối",
      badgeColor: "red",
      variant: "filled",
    } as StatusConfig,
    user_cancelled: {
      label: "Hủy đăng ký",
      badgeColor: "red",
      variant: "outlined",
    } as StatusConfig,
    organizer_cancelled: {
      label: "Organizer hủy đăng ký",
      badgeColor: "red",
      variant: "filled",
    } as StatusConfig,
  },

  // Trận đấu (Match Status) - 8 trạng thái
  match: {
    not_started: {
      label: "Chưa diễn ra",
      badgeColor: "gray",
      variant: "outlined",
    } as StatusConfig,
    upcoming: {
      label: "Sắp diễn ra",
      badgeColor: "blue",
      variant: "filled",
    } as StatusConfig,
    waiting_checkin: {
      label: "Chờ check-in",
      badgeColor: "yellow",
      variant: "outlined",
    } as StatusConfig,
    absent: {
      label: "Vắng mặt",
      badgeColor: "red",
      variant: "outlined",
    } as StatusConfig,
    in_progress: {
      label: "Đang diễn ra",
      badgeColor: "green",
      variant: "filled",
    } as StatusConfig,
    completed: {
      label: "Đã kết thúc",
      badgeColor: "gray",
      variant: "filled",
    } as StatusConfig,
    postponed: {
      label: "Bị hoãn",
      badgeColor: "yellow",
      variant: "filled",
    } as StatusConfig,
    cancelled: {
      label: "Đã huỷ",
      badgeColor: "red",
      variant: "filled",
    } as StatusConfig,
  },

  // Kết quả trận đấu (Match Result Status) - 4 trạng thái
  match_result: {
    no_result: {
      label: "Chưa có kết quả",
      badgeColor: "blue",
      variant: "outlined",
    } as StatusConfig,
    win: {
      label: "Thắng",
      badgeColor: "green",
      variant: "filled",
    } as StatusConfig,
    draw: {
      label: "Hòa",
      badgeColor: "yellow",
      variant: "filled",
    } as StatusConfig,
    loss: {
      label: "Thua",
      badgeColor: "red",
      variant: "filled",
    } as StatusConfig,
  },

  // Trạng thái checkin player (Player Check-in Status) - 3 trạng thái
  checkin: {
    not_checked_in: {
      label: "Chưa check-in",
      badgeColor: "orange",
      variant: "outlined",
    } as StatusConfig,
    checked_in: {
      label: "Đã check-in",
      badgeColor: "green",
      variant: "filled",
    } as StatusConfig,
    absent: {
      label: "Vắng mặt",
      badgeColor: "red",
      variant: "filled",
    } as StatusConfig,
  },
} as const;

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  statusType?: keyof typeof STATUS_MAPPINGS;
  statusValue?: string;
  dismissible?: boolean;
  onDismiss?: () => void;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
}

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  (
    {
      className,
      variant,
      badgeColor,
      size,
      statusType,
      statusValue,
      dismissible,
      onDismiss,
      icon,
      iconPosition = 'right',
      children,
      ...props
    },
    ref,
  ) => {
    // Status mapping logic
    let finalVariant = variant;
    let finalColor = badgeColor;
    let displayText = children;

    if (statusType && statusValue) {
      const statusGroup = STATUS_MAPPINGS[statusType];
      if (statusGroup && statusValue in statusGroup) {
        const statusConfig = statusGroup[
          statusValue as keyof typeof statusGroup
        ] as StatusConfig;
        finalVariant = statusConfig.variant;
        finalColor = statusConfig.badgeColor;
        displayText = statusConfig.label;
      }
    }    // Icon size based on badge size
    const iconSize = size === 'lg' ? 'h-3.5 w-3.5' : 'h-3 w-3';

    // Render icon with proper size
    const renderIcon = (iconElement: React.ReactNode) => {
      if (React.isValidElement(iconElement)) {
        return React.cloneElement(iconElement as React.ReactElement<{className?: string}>, {
          className: cn(iconSize, (iconElement.props as {className?: string}).className)
        });
      }
      return iconElement;
    };

    return (
      <div
        ref={ref}
        className={cn(
          badgeVariants({
            variant: finalVariant,
            badgeColor: finalColor,
            size,
          }),
          className,
        )}
        {...props}
      >
        {/* Left icon */}
        {icon && iconPosition === 'left' && renderIcon(icon)}
        
        {/* Text content */}
        {displayText}
        
        {/* Right icon */}
        {icon && iconPosition === 'right' && renderIcon(icon)}
        
        {/* Dismiss button */}
        {dismissible && (
          <button
            onClick={onDismiss}            className={cn(
              "rounded-full hover:bg-black/10 flex items-center justify-center",
              size === 'lg' ? "ml-1.5 h-4 w-4" : "ml-1 h-3 w-3"
            )}
            aria-label="Dismiss badge"
          >
            <X className={size === 'lg' ? "h-2.5 w-2.5" : "h-2 w-2"} />
          </button>
        )}
      </div>
    );
  },
);

Badge.displayName = "Badge";

export { Badge };
